/**
 * Data requests (wave 116) — what the console is asked to show about an
 * erasure or an export somebody wrote to us about: a request to confirm, and a
 * file to download.
 *
 * Same rule as every module in this directory: TOLERANT OF SHAPE, NEVER OF
 * NAMING. The wire shape is `DataRequestList` in `@nob/schemas`.
 */
import { API_URL, type RawError, call, get, optTextOf, post, textOf } from "./core";

export type DataRequestKind = "erasure" | "export";

export type DataConfirmation = { id: string; kind: DataRequestKind; expiresAt: string };
export type ReadyExport = {
  id: string;
  createdAt: string;
  expiresAt: string;
  /** Null until the person has opened the file once. */
  downloadedAt: string | null;
};
export type DataRequests = { confirmations: DataConfirmation[]; exports: ReadyExport[] };

export async function fetchDataRequests(): Promise<
  ({ ok: true } & DataRequests) | { ok: false; code: string }
> {
  const answer = await call(() => get("/api/v1/me/data-requests"));
  if (!answer) return { ok: false, code: "UNREACHABLE" };
  const { res, body } = answer;
  if (!res.ok) return { ok: false, code: (body as RawError).error?.code ?? "UNKNOWN" };
  const b = (body ?? {}) as Record<string, unknown>;
  const rows = (k: string): Record<string, unknown>[] =>
    Array.isArray(b[k]) ? (b[k] as Record<string, unknown>[]) : [];
  return {
    ok: true,
    confirmations: rows("confirmations").flatMap((r) => {
      const id = textOf(r["id"]);
      const kind = textOf(r["kind"]);
      return id && (kind === "erasure" || kind === "export")
        ? [{ id, kind, expiresAt: textOf(r["expires_at"]) } as DataConfirmation]
        : [];
    }),
    exports: rows("exports").flatMap((r) => {
      const id = textOf(r["id"]);
      return id
        ? [
            {
              id,
              createdAt: textOf(r["created_at"]),
              expiresAt: textOf(r["expires_at"]),
              downloadedAt: optTextOf(r["downloaded_at"]),
            },
          ]
        : [];
    }),
  };
}

/** The person's one click. */
export async function confirmDataRequest(
  id: string,
): Promise<{ ok: true } | { ok: false; code: string }> {
  const answer = await call(() =>
    post(`/api/v1/me/data-requests/${encodeURIComponent(id)}/confirm`, {}),
  );
  if (!answer) return { ok: false, code: "UNREACHABLE" };
  const { res, body } = answer;
  return res.ok ? { ok: true } : { ok: false, code: (body as RawError).error?.code ?? "UNKNOWN" };
}

/** Where the file is: a plain link, so the browser downloads it with the session cookie. */
export function dataExportUrl(id: string): string {
  return `${API_URL}/api/v1/me/data-exports/${encodeURIComponent(id)}`;
}
